import { Box, type BoxProps } from '@mui/material';
import { forwardRef, useId, type ReactNode } from 'react';

export type LogoProduct = 'wsuite' | 'studio' | 'feedati' | 'ai-hub';
export type LogoVariant = 'logo' | 'logomark';
export type LogoColor = 'positive' | 'negative' | 'negative-payoff';

export interface LogoProps extends Omit<BoxProps<'svg'>, 'component' | 'children'> {
  /** Product brand */
  product?: LogoProduct;
  /** `logo` = wordmark with payoff caption, `logomark` = square brand icon */
  variant?: LogoVariant;
  /**
   * Color treatment.
   * - `positive` (default): colored mark for use on light backgrounds
   * - `negative`: white mark for use on dark backgrounds
   * - `negative-payoff`: colored mark with light payoff caption (for use on a colored hero)
   *
   * Falls back to `positive` if a product doesn't define the requested color.
   * `negative-payoff` falls back to `negative` for `logomark` variants (no payoff to invert).
   */
  color?: LogoColor;
}

type LogoRenderer = (id: string) => ReactNode;

// Drop-shadow filter shared across studio/ai-hub colored marks.
const dropShadowFilter = (
  id: string,
  region: { x: string; y: string; width: string; height: string }
) => (
  <filter
    id={id}
    x={region.x}
    y={region.y}
    width={region.width}
    height={region.height}
    filterUnits="userSpaceOnUse"
    colorInterpolationFilters="sRGB"
  >
    <feFlood floodOpacity="0" result="BackgroundImageFix" />
    <feColorMatrix
      in="SourceAlpha"
      type="matrix"
      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
      result="hardAlpha"
    />
    <feOffset />
    <feGaussianBlur stdDeviation="2.5" />
    <feComposite in2="hardAlpha" operator="out" />
    <feColorMatrix
      type="matrix"
      values="0 0 0 0 1 0 0 0 0 0.156863 0 0 0 0 0.129412 0 0 0 0.21 0"
    />
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
  </filter>
);

// ─── STUDIO ──────────────────────────────────────────────────────────────────
const STUDIO_MAIN_MARK_D =
  'M41.99 155.72C29.07 155.72 17 152.32 8.16 146.2V117.47C16.49 126.82 28.73 132.43 40.8 132.43C53.38 132.43 62.22 126.65 62.22 118.32C62.22 112.71 58.14 108.97 48.45 105.57L34.68 100.81C18.02 95.03 9.18 83.81 9.18 68.34C9.18 46.75 26.69 31.28 51.51 31.28C62.56 31.28 73.44 34 81.43 38.93V65.62C73.27 58.48 63.24 54.57 52.7 54.57C41.31 54.57 33.83 59.33 33.83 66.64C33.83 72.42 38.59 77.18 47.6 80.24L61.2 84.83C78.54 90.78 86.87 101.32 86.87 117.47C86.87 140.59 69.19 155.72 41.99 155.72ZM105.961 80.24H90.3208V59.5H105.961V40.29H130.101V59.5H145.741V80.24H130.101V153H105.961V80.24ZM237.772 153H214.312V142.63C208.192 150.79 197.992 155.72 186.092 155.72C165.012 155.72 151.242 141.95 151.242 121.21V59.5H175.382V113.56C175.382 126.65 182.692 134.64 194.762 134.64C206.832 134.64 213.632 126.99 213.632 113.56V59.5H237.772V153ZM323.544 153V139.74C317.084 149.77 306.034 155.55 292.944 155.55C267.444 155.55 248.234 134.47 248.234 106.42C248.234 78.2 267.444 56.95 292.944 56.95C306.204 56.95 317.254 62.56 323.204 71.74V34H347.344V153H323.544ZM298.554 78.03C282.914 78.03 273.224 88.91 273.224 106.25C273.224 123.59 282.914 134.47 298.554 134.47C314.024 134.47 323.714 123.59 323.714 106.42C323.714 88.91 314.024 78.03 298.554 78.03ZM364.309 153V59.5H388.449V153H364.309ZM376.379 51.68C368.049 51.68 362.269 45.9 362.269 37.74C362.269 29.24 368.049 23.29 376.379 23.29C384.709 23.29 390.489 29.24 390.489 37.74C390.489 45.9 384.709 51.68 376.379 51.68ZM448.763 155.55C419.353 155.55 398.613 135.15 398.613 106.25C398.613 77.35 419.353 56.95 448.763 56.95C478.173 56.95 499.083 77.35 499.083 106.25C499.083 135.15 478.173 155.55 448.763 155.55ZM448.763 134.47C464.233 134.47 474.093 123.59 474.093 106.25C474.093 89.08 464.233 78.03 448.763 78.03C433.463 78.03 423.603 89.08 423.603 106.25C423.603 123.59 433.293 134.47 448.763 134.47Z';
const STUDIO_TM_BAR_D =
  'M526.132 36V40.6H520.888V83.15H526.132V87.75H515V36H526.132ZM569.372 36V87.75H558.24V83.15H563.484V40.6H558.24V36H569.372ZM546.556 65.992L542.232 55.412L537.816 65.992H546.556ZM548.948 71.788H535.424L532.94 77.906H525.856L539.886 45.844H544.486L558.516 77.906H551.432L548.948 71.788Z';
const STUDIO_TM_PLUS_D =
  'M582.946 36.536H588.65V45.138H597.114V50.658H588.65V59.306H582.946V50.658H574.39V45.138H582.946V36.536Z';
const STUDIO_PAYOFF_D =
  'M20.904 225H14.73V195.6H25.356C31.194 195.6 35.058 199.086 35.058 204.336C35.058 209.544 31.278 212.862 25.356 212.862H20.904V225ZM25.23 207.99C27.372 207.99 28.758 206.52 28.758 204.252C28.758 201.942 27.372 200.472 25.188 200.472H20.904V207.99H25.23ZM47.8828 225.63C40.6168 225.63 35.4928 220.59 35.4928 213.45C35.4928 206.31 40.6168 201.27 47.8828 201.27C55.1488 201.27 60.3148 206.31 60.3148 213.45C60.3148 220.59 55.1488 225.63 47.8828 225.63ZM47.8828 220.422C51.7048 220.422 54.1408 217.734 54.1408 213.45C54.1408 209.208 51.7048 206.478 47.8828 206.478C44.1028 206.478 41.6668 209.208 41.6668 213.45C41.6668 217.734 44.0608 220.422 47.8828 220.422ZM66.2725 201.9L70.8925 215.592L75.5545 201.9H79.9645L84.6685 215.718L89.3305 201.9H95.6725L86.8105 225H82.6525L77.7805 210.72L72.9505 225H68.7925L59.9305 201.9H66.2725ZM116.062 223.236C113.92 224.748 110.812 225.672 107.83 225.672C100.564 225.672 95.3138 220.506 95.3138 213.282C95.3138 206.268 100.144 201.228 106.906 201.228C113.542 201.228 117.994 206.058 117.994 213.282C117.994 215.004 117.742 215.886 117.742 215.886H101.614C102.328 218.7 104.89 220.548 108.334 220.548C110.77 220.548 113.29 219.708 116.062 217.692V223.236ZM101.446 211.182H112.366C112.114 208.116 110.224 206.142 107.074 206.142C103.924 206.142 101.698 208.2 101.446 211.182ZM134.939 207.906C134.225 207.57 133.427 207.486 132.209 207.486C128.807 207.486 127.127 209.502 127.127 212.19V225H121.163V201.9H126.959V205.134C128.219 202.866 130.781 201.228 133.553 201.228C134.141 201.228 134.603 201.354 134.939 201.48V207.906ZM155.211 223.236C153.069 224.748 149.961 225.672 146.979 225.672C139.713 225.672 134.463 220.506 134.463 213.282C134.463 206.268 139.293 201.228 146.055 201.228C152.691 201.228 157.143 206.058 157.143 213.282C157.143 215.004 156.891 215.886 156.891 215.886H140.763C141.477 218.7 144.039 220.548 147.483 220.548C149.919 220.548 152.439 219.708 155.211 217.692V223.236ZM140.595 211.182H151.515C151.263 208.116 149.373 206.142 146.223 206.142C143.073 206.142 140.847 208.2 140.595 211.182ZM177.278 225V221.724C175.682 224.202 172.952 225.63 169.718 225.63C163.418 225.63 158.672 220.422 158.672 213.492C158.672 206.52 163.418 201.27 169.718 201.27C172.994 201.27 175.724 202.656 177.194 204.924V195.6H183.158V225H177.278ZM171.104 206.478C167.24 206.478 164.846 209.166 164.846 213.45C164.846 217.734 167.24 220.422 171.104 220.422C174.926 220.422 177.32 217.734 177.32 213.492C177.32 209.166 174.926 206.478 171.104 206.478ZM202.02 225H196.14V195.6H202.104V204.882C203.616 202.53 206.43 201.27 209.58 201.27C215.88 201.27 220.626 206.478 220.626 213.492C220.626 220.422 215.88 225.63 210 225.63C206.43 225.63 203.658 224.202 202.02 221.682V225ZM201.978 213.492C201.978 217.734 204.372 220.422 208.194 220.422C212.058 220.422 214.452 217.734 214.452 213.45C214.452 209.166 212.058 206.478 208.194 206.478C204.372 206.478 201.978 209.166 201.978 213.492ZM226.63 201.9L232.216 216.054L237.718 201.9H244.06L230.872 233.442H224.446L229.024 223.026L220.162 201.9H226.63ZM268.612 195.6H272.35L278.062 213.408L283.186 195.6H289.36L280.078 225H276.256L270.502 207.444L264.706 225H260.884L251.602 195.6H257.776L262.9 213.408L268.612 195.6ZM291.594 225V201.9H297.558V225H291.594ZM294.576 199.968C292.518 199.968 291.09 198.54 291.09 196.524C291.09 194.424 292.518 192.954 294.576 192.954C296.634 192.954 298.062 194.424 298.062 196.524C298.062 198.54 296.634 199.968 294.576 199.968ZM303.768 207.024H299.904V201.9H303.768V197.154H309.732V201.9H313.596V207.024H309.732V225H303.768V207.024ZM328.899 211.182C328.899 208.326 326.673 206.478 323.271 206.478C320.751 206.478 317.811 207.528 315.585 209.04V203.286C317.937 201.984 321.045 201.228 323.943 201.228C330.411 201.228 334.569 204.84 334.569 210.51V225H329.109V223.194C327.681 224.706 325.413 225.672 322.893 225.672C318.189 225.672 314.913 222.522 314.913 217.986C314.913 213.366 318.357 210.132 323.229 210.132C325.623 210.132 327.639 210.93 328.899 212.274V211.182ZM320.793 217.776C320.793 219.624 322.473 220.926 324.867 220.926C327.261 220.926 328.941 219.624 328.941 217.776C328.941 215.886 327.261 214.584 324.909 214.584C322.515 214.584 320.793 215.886 320.793 217.776ZM338.957 225V201.9H344.921V225H338.957ZM341.939 199.968C339.881 199.968 338.453 198.54 338.453 196.524C338.453 194.424 339.881 192.954 341.939 192.954C343.997 192.954 345.425 194.424 345.425 196.524C345.425 198.54 343.997 199.968 341.939 199.968ZM355.584 195.6V225H349.62V195.6H355.584ZM379.483 223.236C377.341 224.748 374.233 225.672 371.251 225.672C363.985 225.672 358.735 220.506 358.735 213.282C358.735 206.268 363.565 201.228 370.327 201.228C376.963 201.228 381.415 206.058 381.415 213.282C381.415 215.004 381.163 215.886 381.163 215.886H365.035C365.749 218.7 368.311 220.548 371.755 220.548C374.191 220.548 376.711 219.708 379.483 217.692V223.236ZM364.867 211.182H375.787C375.535 208.116 373.645 206.142 370.495 206.142C367.345 206.142 365.119 208.2 364.867 211.182ZM398.361 207.906C397.647 207.57 396.849 207.486 395.631 207.486C392.229 207.486 390.549 209.502 390.549 212.19V225H384.585V201.9H390.381V205.134C391.641 202.866 394.203 201.228 396.975 201.228C397.563 201.228 398.025 201.354 398.361 201.48V207.906Z';
const STUDIO_LOGOMARK_S_D =
  'M80.122 131.016C70.546 131.016 61.6 128.496 55.048 123.96V102.666C61.222 109.596 70.294 113.754 79.24 113.754C88.564 113.754 95.116 109.47 95.116 103.296C95.116 99.138 92.092 96.366 84.91 93.846L74.704 90.318C62.356 86.034 55.804 77.718 55.804 66.252C55.804 50.25 68.782 38.784 87.178 38.784C95.368 38.784 103.432 40.8 109.354 44.454V64.236C103.306 58.944 95.872 56.046 88.06 56.046C79.618 56.046 74.074 59.574 74.074 64.992C74.074 69.276 77.602 72.804 84.28 75.072L94.36 78.474C107.212 82.884 113.386 90.696 113.386 102.666C113.386 119.802 100.282 131.016 80.122 131.016Z';

// ─── AI HUB ──────────────────────────────────────────────────────────────────
const AI_HUB_MAIN_MARK_D =
  'M1.87 153L54.06 34H71.06L123.25 153H96.9L87.55 130.39H37.57L28.22 153H1.87ZM46.24 108.97H78.88L62.56 69.53L46.24 108.97ZM128.905 34H153.895V153H128.905V34ZM202.087 153V34H227.077V81.94H273.317V34H298.307V153H273.317V104.04H227.077V153H202.087ZM400.104 153H376.644V142.63C370.524 150.79 360.324 155.72 348.424 155.72C327.344 155.72 313.574 141.95 313.574 121.21V59.5H337.714V113.56C337.714 126.65 345.024 134.64 357.094 134.64C369.164 134.64 375.964 126.99 375.964 113.56V59.5H400.104V153ZM440.842 153H417.042V34H441.182V71.57C447.302 62.05 458.692 56.95 471.442 56.95C496.942 56.95 516.152 78.03 516.152 106.42C516.152 134.47 496.942 155.55 473.142 155.55C458.692 155.55 447.472 149.77 440.842 139.57V153ZM440.672 106.42C440.672 123.59 450.362 134.47 465.832 134.47C481.472 134.47 491.162 123.59 491.162 106.25C491.162 88.91 481.472 78.03 465.832 78.03C450.362 78.03 440.672 88.91 440.672 106.42Z';
const AI_HUB_TM_BAR_D =
  'M543.132 36V40.6H537.888V83.15H543.132V87.75H532V36H543.132ZM586.372 36V87.75H575.24V83.15H580.484V40.6H575.24V36H586.372ZM563.556 65.992L559.232 55.412L554.816 65.992H563.556ZM565.948 71.788H552.424L549.94 77.906H542.856L556.886 45.844H561.486L575.516 77.906H568.432L565.948 71.788Z';
const AI_HUB_TM_PLUS_D =
  'M599.946 36.536H605.65V45.138H614.114V50.658H605.65V59.306H599.946V50.658H591.39V45.138H599.946V36.536Z';
const AI_HUB_PAYOFF_D =
  'M21.2434 225H15.0694V195.6H25.6954C31.5334 195.6 35.3974 199.086 35.3974 204.336C35.3974 209.544 31.6174 212.862 25.6954 212.862H21.2434V225ZM25.5694 207.99C27.7114 207.99 29.0974 206.52 29.0974 204.252C29.0974 201.942 27.7114 200.472 25.5274 200.472H21.2434V207.99H25.5694ZM48.2221 225.63C40.9561 225.63 35.8321 220.59 35.8321 213.45C35.8321 206.31 40.9561 201.27 48.2221 201.27C55.4881 201.27 60.6541 206.31 60.6541 213.45C60.6541 220.59 55.4881 225.63 48.2221 225.63ZM48.2221 220.422C52.0441 220.422 54.4801 217.734 54.4801 213.45C54.4801 209.208 52.0441 206.478 48.2221 206.478C44.4421 206.478 42.0061 209.208 42.0061 213.45C42.0061 217.734 44.4001 220.422 48.2221 220.422ZM66.6119 201.9L71.2319 215.592L75.8939 201.9H80.3039L85.0079 215.718L89.6699 201.9H96.0119L87.1499 225H82.9919L78.1199 210.72L73.2899 225H69.1319L60.2699 201.9H66.6119ZM116.401 223.236C114.259 224.748 111.151 225.672 108.169 225.672C100.903 225.672 95.6532 220.506 95.6532 213.282C95.6532 206.268 100.483 201.228 107.245 201.228C113.881 201.228 118.333 206.058 118.333 213.282C118.333 215.004 118.081 215.886 118.081 215.886H101.953C102.667 218.7 105.229 220.548 108.673 220.548C111.109 220.548 113.629 219.708 116.401 217.692V223.236ZM101.785 211.182H112.705C112.453 208.116 110.563 206.142 107.413 206.142C104.263 206.142 102.037 208.2 101.785 211.182ZM135.279 207.906C134.565 207.57 133.767 207.486 132.549 207.486C129.147 207.486 127.467 209.502 127.467 212.19V225H121.503V201.9H127.299V205.134C128.559 202.866 131.121 201.228 133.893 201.228C134.481 201.228 134.943 201.354 135.279 201.48V207.906ZM155.55 223.236C153.408 224.748 150.3 225.672 147.318 225.672C140.052 225.672 134.802 220.506 134.802 213.282C134.802 206.268 139.632 201.228 146.394 201.228C153.03 201.228 157.482 206.058 157.482 213.282C157.482 215.004 157.23 215.886 157.23 215.886H141.102C141.816 218.7 144.378 220.548 147.822 220.548C150.258 220.548 152.778 219.708 155.55 217.692V223.236ZM140.934 211.182H151.854C151.602 208.116 149.712 206.142 146.562 206.142C143.412 206.142 141.186 208.2 140.934 211.182ZM177.618 225V221.724C176.022 224.202 173.292 225.63 170.058 225.63C163.758 225.63 159.012 220.422 159.012 213.492C159.012 206.52 163.758 201.27 170.058 201.27C173.334 201.27 176.064 202.656 177.534 204.924V195.6H183.498V225H177.618ZM171.444 206.478C167.58 206.478 165.186 209.166 165.186 213.45C165.186 217.734 167.58 220.422 171.444 220.422C175.266 220.422 177.66 217.734 177.66 213.492C177.66 209.166 175.266 206.478 171.444 206.478ZM202.36 225H196.48V195.6H202.444V204.882C203.956 202.53 206.77 201.27 209.92 201.27C216.22 201.27 220.966 206.478 220.966 213.492C220.966 220.422 216.22 225.63 210.34 225.63C206.77 225.63 203.998 224.202 202.36 221.682V225ZM202.318 213.492C202.318 217.734 204.712 220.422 208.534 220.422C212.398 220.422 214.792 217.734 214.792 213.45C214.792 209.166 212.398 206.478 208.534 206.478C204.712 206.478 202.318 209.166 202.318 213.492ZM226.969 201.9L232.555 216.054L238.057 201.9H244.399L231.211 233.442H224.785L229.363 223.026L220.501 201.9H226.969ZM268.951 195.6H272.689L278.401 213.408L283.525 195.6H289.699L280.417 225H276.595L270.841 207.444L265.045 225H261.223L251.941 195.6H258.115L263.239 213.408L268.951 195.6ZM291.934 225V201.9H297.898V225H291.934ZM294.916 199.968C292.858 199.968 291.43 198.54 291.43 196.524C291.43 194.424 292.858 192.954 294.916 192.954C296.974 192.954 298.402 194.424 298.402 196.524C298.402 198.54 296.974 199.968 294.916 199.968ZM304.107 207.024H300.243V201.9H304.107V197.154H310.071V201.9H313.935V207.024H310.071V225H304.107V207.024ZM329.238 211.182C329.238 208.326 327.012 206.478 323.61 206.478C321.09 206.478 318.15 207.528 315.924 209.04V203.286C318.276 201.984 321.384 201.228 324.282 201.228C330.75 201.228 334.908 204.84 334.908 210.51V225H329.448V223.194C328.02 224.706 325.752 225.672 323.232 225.672C318.528 225.672 315.252 222.522 315.252 217.986C315.252 213.366 318.696 210.132 323.568 210.132C325.962 210.132 327.978 210.93 329.238 212.274V211.182ZM321.132 217.776C321.132 219.624 322.812 220.926 325.206 220.926C327.6 220.926 329.28 219.624 329.28 217.776C329.28 215.886 327.6 214.584 325.248 214.584C322.854 214.584 321.132 215.886 321.132 217.776ZM339.296 225V201.9H345.26V225H339.296ZM342.278 199.968C340.22 199.968 338.792 198.54 338.792 196.524C338.792 194.424 340.22 192.954 342.278 192.954C344.336 192.954 345.764 194.424 345.764 196.524C345.764 198.54 344.336 199.968 342.278 199.968ZM355.923 195.6V225H349.959V195.6H355.923ZM379.823 223.236C377.681 224.748 374.573 225.672 371.591 225.672C364.325 225.672 359.075 220.506 359.075 213.282C359.075 206.268 363.905 201.228 370.667 201.228C377.303 201.228 381.755 206.058 381.755 213.282C381.755 215.004 381.503 215.886 381.503 215.886H365.375C366.089 218.7 368.651 220.548 372.095 220.548C374.531 220.548 377.051 219.708 379.823 217.692V223.236ZM365.207 211.182H376.127C375.875 208.116 373.985 206.142 370.835 206.142C367.685 206.142 365.459 208.2 365.207 211.182ZM398.7 207.906C397.986 207.57 397.188 207.486 395.97 207.486C392.568 207.486 390.888 209.502 390.888 212.19V225H384.924V201.9H390.72V205.134C391.98 202.866 394.542 201.228 397.314 201.228C397.902 201.228 398.364 201.354 398.7 201.48V207.906Z';
const AI_HUB_LOGOMARK_AI_D =
  'M24.7551 129L63.4371 40.8H76.0371L114.719 129H95.1891L88.2591 112.242H51.2151L44.2851 129H24.7551ZM57.6411 96.366H81.8331L69.7371 67.134L57.6411 96.366ZM117.876 40.8H136.398V129H117.876V40.8Z';

// ─── Renderers ───────────────────────────────────────────────────────────────
const renderStudioLogo: LogoRenderer = (id) => {
  // For all 3 colors of `studio` `logo`, the same renderer handles all colors via id-suffixed gradients.
  // (Color branching happens in the wrapper function below — this is a no-op placeholder)
  void id;
  return null;
};

const studioLogoFor = (color: LogoColor): LogoRenderer => (id) => {
  const isFullyNegative = color === 'negative';
  const lightCaption = color !== 'positive';
  return (
    <>
      {/* Payoff caption */}
      <path d={STUDIO_PAYOFF_D} fill={`url(#${id}-caption)`} />
      <g clipPath={`url(#${id}-clip)`}>
        {isFullyNegative ? (
          <>
            <path d={STUDIO_MAIN_MARK_D} fill={`url(#${id}-mark)`} />
            <path d={STUDIO_TM_BAR_D} fill={`url(#${id}-tm)`} />
            <path d={STUDIO_TM_PLUS_D} fill={`url(#${id}-tm)`} />
          </>
        ) : (
          <>
            <g filter={`url(#${id}-shadow-mark)`}>
              <path d={STUDIO_MAIN_MARK_D} fill={`url(#${id}-radial)`} />
            </g>
            <g filter={`url(#${id}-shadow-tm)`}>
              <path d={STUDIO_TM_BAR_D} fill="#9D5FFF" />
              <path d={STUDIO_TM_PLUS_D} fill="#9D5FFF" />
            </g>
          </>
        )}
      </g>
      <defs>
        <linearGradient
          id={`${id}-caption`}
          x1="205"
          y1="196"
          x2="205.5"
          y2="229"
          gradientUnits="userSpaceOnUse"
        >
          {lightCaption ? (
            <>
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </>
          ) : (
            <>
              <stop stopColor="#4D4D4D" />
              <stop offset="1" stopColor="#000000" />
            </>
          )}
        </linearGradient>
        {isFullyNegative ? (
          <>
            <linearGradient
              id={`${id}-mark`}
              x1="250.85"
              y1="36.4286"
              x2="257.14"
              y2="169.734"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
            <linearGradient
              id={`${id}-tm`}
              x1="555.951"
              y1="47.0893"
              x2="559.502"
              y2="87.4469"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
          </>
        ) : (
          <>
            {dropShadowFilter(`${id}-shadow-mark`, {
              x: '3.16',
              y: '18.29',
              width: '500.923',
              height: '142.43',
            })}
            {dropShadowFilter(`${id}-shadow-tm`, {
              x: '510',
              y: '31',
              width: '92.1138',
              height: '61.75',
            })}
            <radialGradient
              id={`${id}-radial`}
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(26 226) rotate(-28.5718) scale(472.547 1398.18)"
            >
              <stop offset="0.200144" stopColor="#FF2821" />
              <stop offset="0.33982" stopColor="#FF3B30" />
              <stop offset="0.701637" stopColor="#9D5FFF" />
            </radialGradient>
          </>
        )}
        <clipPath id={`${id}-clip`}>
          <rect width="597.114" height="234" fill="white" />
        </clipPath>
      </defs>
    </>
  );
};

const studioLogomarkFor = (color: LogoColor): LogoRenderer => (id) => {
  const isNegative = color === 'negative' || color === 'negative-payoff';
  return (
    <>
      <rect
        x={isNegative ? '0.5' : '0'}
        y={isNegative ? '0.5' : '0'}
        width={isNegative ? '167' : '168'}
        height={isNegative ? '167' : '168'}
        rx={isNegative ? '57.5' : '58'}
        fill={`url(#${id}-bg)`}
      />
      <rect
        x="0.5"
        y="0.5"
        width="167"
        height="167"
        rx="57.5"
        stroke="white"
        strokeOpacity={isNegative ? '1' : '0.5'}
      />
      <g filter={`url(#${id}-shadow)`}>
        <path d={STUDIO_LOGOMARK_S_D} fill={`url(#${id}-fg)`} />
      </g>
      <defs>
        {dropShadowFilter(`${id}-shadow`, {
          x: '50.048',
          y: '33.784',
          width: '68.338',
          height: '102.232',
        })}
        {isNegative ? (
          <>
            <linearGradient
              id={`${id}-bg`}
              x1="83.7829"
              y1="36"
              x2="101.863"
              y2="165.506"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
            <linearGradient
              id={`${id}-fg`}
              x1="84"
              y1="16"
              x2="84"
              y2="142"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FF2821" />
              <stop offset="1" stopColor="#9D5FFF" />
            </linearGradient>
          </>
        ) : (
          <>
            <linearGradient
              id={`${id}-bg`}
              x1="84"
              y1="0"
              x2="84"
              y2="168"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FF2821" />
              <stop offset="1" stopColor="#9D5FFF" />
            </linearGradient>
            <linearGradient
              id={`${id}-fg`}
              x1="83.9096"
              y1="43"
              x2="107.316"
              y2="136.141"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
          </>
        )}
      </defs>
    </>
  );
};

const aiHubLogoFor = (color: LogoColor): LogoRenderer => (id) => {
  const isFullyNegative = color === 'negative';
  const lightCaption = color !== 'positive';
  return (
    <>
      <path d={AI_HUB_PAYOFF_D} fill={`url(#${id}-caption)`} />
      <g clipPath={`url(#${id}-clip)`}>
        {isFullyNegative ? (
          <>
            <path d={AI_HUB_MAIN_MARK_D} fill={`url(#${id}-mark)`} />
            <path d={AI_HUB_TM_BAR_D} fill={`url(#${id}-tm)`} />
            <path d={AI_HUB_TM_PLUS_D} fill={`url(#${id}-tm)`} />
          </>
        ) : (
          <>
            <g filter={`url(#${id}-shadow-mark)`}>
              <path d={AI_HUB_MAIN_MARK_D} fill={`url(#${id}-radial)`} />
            </g>
            <g filter={`url(#${id}-shadow-tm)`}>
              <path d={AI_HUB_TM_BAR_D} fill="#9D5FFF" />
              <path d={AI_HUB_TM_PLUS_D} fill="#9D5FFF" />
            </g>
          </>
        )}
      </g>
      <defs>
        <linearGradient
          id={`${id}-caption`}
          x1="205.339"
          y1="196"
          x2="205.839"
          y2="229"
          gradientUnits="userSpaceOnUse"
        >
          {lightCaption ? (
            <>
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </>
          ) : (
            <>
              <stop stopColor="#4D4D4D" />
              <stop offset="1" stopColor="#000000" />
            </>
          )}
        </linearGradient>
        {isFullyNegative ? (
          <>
            <linearGradient
              id={`${id}-mark`}
              x1="259.328"
              y1="36.4286"
              x2="265.413"
              y2="169.753"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
            <linearGradient
              id={`${id}-tm`}
              x1="572.951"
              y1="47.0893"
              x2="576.502"
              y2="87.4469"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
          </>
        ) : (
          <>
            {dropShadowFilter(`${id}-shadow-mark`, {
              x: '-3.12988',
              y: '29',
              width: '524.282',
              height: '131.72',
            })}
            {dropShadowFilter(`${id}-shadow-tm`, {
              x: '527',
              y: '31',
              width: '92.114',
              height: '61.75',
            })}
            <radialGradient
              id={`${id}-radial`}
              cx="0"
              cy="0"
              r="1"
              gradientTransform="matrix(429.026 -226 691.294 1227.91 26.8788 226)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.200144" stopColor="#FF2821" />
              <stop offset="0.33982" stopColor="#FF3B30" />
              <stop offset="0.701637" stopColor="#9D5FFF" />
            </radialGradient>
          </>
        )}
        <clipPath id={`${id}-clip`}>
          <rect width="614" height="234" fill="white" />
        </clipPath>
      </defs>
    </>
  );
};

const aiHubLogomarkFor = (color: LogoColor): LogoRenderer => (id) => {
  const isNegative = color === 'negative' || color === 'negative-payoff';
  return (
    <>
      <rect
        x={isNegative ? '0.5' : '0'}
        y={isNegative ? '0.5' : '0'}
        width={isNegative ? '167' : '168'}
        height={isNegative ? '167' : '168'}
        rx={isNegative ? '57.5' : '58'}
        fill={`url(#${id}-bg)`}
      />
      <rect
        x="0.5"
        y="0.5"
        width="167"
        height="167"
        rx="57.5"
        stroke="white"
        strokeOpacity={isNegative ? '1' : '0.5'}
      />
      <g filter={`url(#${id}-shadow)`}>
        <path d={AI_HUB_LOGOMARK_AI_D} fill={`url(#${id}-fg)`} />
      </g>
      <defs>
        {dropShadowFilter(`${id}-shadow`, {
          x: '19.7551',
          y: '35.8',
          width: '121.642',
          height: '98.2',
        })}
        {isNegative ? (
          <>
            <linearGradient
              id={`${id}-bg`}
              x1="83.7829"
              y1="36"
              x2="101.863"
              y2="165.506"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
            <linearGradient
              id={`${id}-fg`}
              x1="84"
              y1="16"
              x2="84"
              y2="142"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FF2821" />
              <stop offset="1" stopColor="#9D5FFF" />
            </linearGradient>
          </>
        ) : (
          <>
            <linearGradient
              id={`${id}-bg`}
              x1="84"
              y1="0"
              x2="84"
              y2="168"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FF2821" />
              <stop offset="1" stopColor="#9D5FFF" />
            </linearGradient>
            <linearGradient
              id={`${id}-fg`}
              x1="83.8424"
              y1="43"
              x2="97.8295"
              y2="140.006"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="white" />
              <stop offset="1" stopColor="#E8E8E8" />
            </linearGradient>
          </>
        )}
      </defs>
    </>
  );
};

// ─── WSuite & FeeDati (simple inline wordmarks; only `positive` color) ───────
const wsuiteLogo: LogoRenderer = () => (
  <>
    <path
      d="M0.957 2.76H3.729V3.838H2.387V13.122H3.729V14.2H0.957V2.76ZM7.97844 4.3H8.95744L10.4534 8.964L11.7954 4.3H13.4124L10.9814 12H9.98044L8.47344 7.402L6.95544 12H5.95444L3.52344 4.3H5.14044L6.48244 8.964L7.97844 4.3ZM15.9729 12.176C15.1369 12.176 14.3559 11.956 13.7839 11.56V9.701C14.3229 10.306 15.1149 10.669 15.8959 10.669C16.7099 10.669 17.2819 10.295 17.2819 9.756C17.2819 9.393 17.0179 9.151 16.3909 8.931L15.4999 8.623C14.4219 8.249 13.8499 7.523 13.8499 6.522C13.8499 5.125 14.9829 4.124 16.5889 4.124C17.3039 4.124 18.0079 4.3 18.5249 4.619V6.346C17.9969 5.884 17.3479 5.631 16.6659 5.631C15.9289 5.631 15.4449 5.939 15.4449 6.412C15.4449 6.786 15.7529 7.094 16.3359 7.292L17.2159 7.589C18.3379 7.974 18.8769 8.656 18.8769 9.701C18.8769 11.197 17.7329 12.176 15.9729 12.176ZM25.4205 12H23.9025V11.329C23.5065 11.857 22.8465 12.176 22.0765 12.176C20.7125 12.176 19.8215 11.285 19.8215 9.943V5.95H21.3835V9.448C21.3835 10.295 21.8565 10.812 22.6375 10.812C23.4185 10.812 23.8585 10.317 23.8585 9.448V5.95H25.4205V12ZM26.7781 12V5.95H28.3401V12H26.7781ZM27.5591 5.444C27.0201 5.444 26.6461 5.07 26.6461 4.542C26.6461 3.992 27.0201 3.607 27.5591 3.607C28.0981 3.607 28.4721 3.992 28.4721 4.542C28.4721 5.07 28.0981 5.444 27.5591 5.444ZM30.0712 7.292H29.0592V5.95H30.0712V4.707H31.6332V5.95H32.6452V7.292H31.6332V12H30.0712V7.292ZM38.386 11.538C37.825 11.934 37.011 12.176 36.23 12.176C34.327 12.176 32.952 10.823 32.952 8.931C32.952 7.094 34.217 5.774 35.988 5.774C37.726 5.774 38.892 7.039 38.892 8.931C38.892 9.382 38.826 9.613 38.826 9.613H34.602C34.789 10.35 35.46 10.834 36.362 10.834C37 10.834 37.66 10.614 38.386 10.086V11.538ZM34.558 8.381H37.418C37.352 7.578 36.857 7.061 36.032 7.061C35.207 7.061 34.624 7.6 34.558 8.381ZM41.6447 2.76V14.2H38.8727V13.122H40.2147V3.838H38.8727V2.76H41.6447Z"
      fill="black"
    />
    <path
      d="M44.6972 2.88174H46.0612V4.93874H48.0852V6.25874H46.0612V8.32674H44.6972V6.25874H42.6512V4.93874H44.6972V2.88174Z"
      fill="#FF78F3"
    />
  </>
);

const wsuiteLogomark: LogoRenderer = () => (
  <>
    <path
      d="M0.957 2.76H3.729V3.838H2.387V13.122H3.729V14.2H0.957V2.76ZM7.97844 4.3H8.95744L10.4534 8.964L11.7954 4.3H13.4124L10.9814 12H9.98044L8.47344 7.402L6.95544 12H5.95444L3.52344 4.3H5.14044L6.48244 8.964L7.97844 4.3ZM15.9816 2.76V14.2H13.2096V13.122H14.5516V3.838H13.2096V2.76H15.9816Z"
      fill="black"
    />
    <path
      d="M18.6972 2.88174H20.0612V4.93874H22.0852V6.25874H20.0612V8.32674H18.6972V6.25874H16.6512V4.93874H18.6972V2.88174Z"
      fill="#FF78F3"
    />
  </>
);

const feedatiLogo: LogoRenderer = () => (
  <>
    <path
      d="M0.957 2.76H3.729V3.838H2.387V13.122H3.729V14.2H0.957V2.76ZM4.21695 12V4.3H9.65095V5.73H5.83395V7.523H9.11195V8.953H5.83395V12H4.21695ZM15.1399 11.538C14.5789 11.934 13.7649 12.176 12.9839 12.176C11.0809 12.176 9.70587 10.823 9.70587 8.931C9.70587 7.094 10.9709 5.774 12.7419 5.774C14.4799 5.774 15.6459 7.039 15.6459 8.931C15.6459 9.382 15.5799 9.613 15.5799 9.613H11.3559C11.5429 10.35 12.2139 10.834 13.1159 10.834C13.7539 10.834 14.4139 10.614 15.1399 10.086V11.538ZM11.3119 8.381H14.1719C14.1059 7.578 13.6109 7.061 12.7859 7.061C11.9609 7.061 11.3779 7.6 11.3119 8.381ZM21.5637 11.538C21.0027 11.934 20.1887 12.176 19.4077 12.176C17.5047 12.176 16.1297 10.823 16.1297 8.931C16.1297 7.094 17.3947 5.774 19.1657 5.774C20.9037 5.774 22.0697 7.039 22.0697 8.931C22.0697 9.382 22.0037 9.613 22.0037 9.613H17.7797C17.9667 10.35 18.6377 10.834 19.5397 10.834C20.1777 10.834 20.8377 10.614 21.5637 10.086V11.538ZM17.7357 8.381H20.5957C20.5297 7.578 20.0347 7.061 19.2097 7.061C18.3847 7.061 17.8017 7.6 17.7357 8.381ZM24.6328 5.708V10.592H25.5788C27.0968 10.592 28.0208 9.624 28.0208 8.15C28.0208 6.698 27.0748 5.708 25.5788 5.708H24.6328ZM25.7218 4.3C28.0758 4.3 29.6708 6.005 29.6708 8.15C29.6708 10.306 28.0978 12 25.7218 12H23.0158V4.3H25.7218ZM34.0509 8.381C34.0509 7.633 33.4679 7.149 32.5769 7.149C31.9169 7.149 31.1469 7.424 30.5639 7.82V6.313C31.1799 5.972 31.9939 5.774 32.7529 5.774C34.4469 5.774 35.5359 6.72 35.5359 8.205V12H34.1059V11.527C33.7319 11.923 33.1379 12.176 32.4779 12.176C31.2459 12.176 30.3879 11.351 30.3879 10.163C30.3879 8.953 31.2899 8.106 32.5659 8.106C33.1929 8.106 33.7209 8.315 34.0509 8.667V8.381ZM31.9279 10.108C31.9279 10.592 32.3679 10.933 32.9949 10.933C33.6219 10.933 34.0619 10.592 34.0619 10.108C34.0619 9.613 33.6219 9.272 33.0059 9.272C32.3789 9.272 31.9279 9.613 31.9279 10.108ZM37.118 7.292H36.106V5.95H37.118V4.707H38.68V5.95H39.692V7.292H38.68V12H37.118V7.292ZM40.41 12V5.95H41.972V12H40.41ZM41.191 5.444C40.652 5.444 40.278 5.07 40.278 4.542C40.278 3.992 40.652 3.607 41.191 3.607C41.73 3.607 42.104 3.992 42.104 4.542C42.104 5.07 41.73 5.444 41.191 5.444ZM45.2219 2.76V14.2H42.4499V13.122H43.7919V3.838H42.4499V2.76H45.2219Z"
      fill="black"
    />
    <path
      d="M48.6971 2.88174H50.0611V4.93874H52.0851V6.25874H50.0611V8.32674H48.6971V6.25874H46.6511V4.93874H48.6971V2.88174Z"
      fill="#FF2821"
    />
  </>
);

const feedatiLogomark: LogoRenderer = () => (
  <>
    <path
      d="M0.957 2.76H3.729V3.838H2.387V13.122H3.729V14.2H0.957V2.76ZM4.21695 12V4.3H9.65095V5.73H5.83395V7.523H9.11195V8.953H5.83395V12H4.21695ZM12.4904 2.76V14.2H9.71841V13.122H11.0604V3.838H9.71841V2.76H12.4904Z"
      fill="black"
    />
    <path
      d="M15.6972 2.88174H17.0612V4.93874H19.0852V6.25874H17.0612V8.32674H15.6972V6.25874H13.6512V4.93874H15.6972V2.88174Z"
      fill="#FF2821"
    />
  </>
);

// ─── Content map ─────────────────────────────────────────────────────────────
type ProductContent = Record<LogoVariant, Partial<Record<LogoColor, LogoRenderer>>>;

const svgContent: Record<LogoProduct, ProductContent> = {
  wsuite: {
    logo: { positive: wsuiteLogo },
    logomark: { positive: wsuiteLogomark },
  },
  studio: {
    logo: {
      positive: studioLogoFor('positive'),
      negative: studioLogoFor('negative'),
      'negative-payoff': studioLogoFor('negative-payoff'),
    },
    logomark: {
      positive: studioLogomarkFor('positive'),
      negative: studioLogomarkFor('negative'),
    },
  },
  feedati: {
    logo: { positive: feedatiLogo },
    logomark: { positive: feedatiLogomark },
  },
  'ai-hub': {
    logo: {
      positive: aiHubLogoFor('positive'),
      negative: aiHubLogoFor('negative'),
      'negative-payoff': aiHubLogoFor('negative-payoff'),
    },
    logomark: {
      positive: aiHubLogomarkFor('positive'),
      negative: aiHubLogomarkFor('negative'),
    },
  },
};

const viewBoxMap: Record<LogoProduct, Record<LogoVariant, string>> = {
  wsuite: { logo: '0 0 51 16', logomark: '0 0 25 16' },
  studio: { logo: '0 0 597.114 234', logomark: '0 0 168 168' },
  feedati: { logo: '0 0 55 16', logomark: '0 0 22 16' },
  'ai-hub': { logo: '0 0 614 234', logomark: '0 0 168 168' },
};

// Avoid placeholder warning from unused renderer constant.
void renderStudioLogo;

const resolveColor = (
  product: LogoProduct,
  variant: LogoVariant,
  color: LogoColor
): LogoColor => {
  const variantContent = svgContent[product][variant];
  if (variantContent[color]) return color;
  // For logomark, treat negative-payoff as negative (no payoff to invert).
  if (variant === 'logomark' && color === 'negative-payoff' && variantContent.negative) {
    return 'negative';
  }
  return 'positive';
};

/**
 * Logo component for the Forest product suite.
 *
 * @example
 * <Logo product="studio" variant="logo" color="positive" />
 * <Logo product="studio" variant="logo" color="negative" />
 * <Logo product="ai-hub" variant="logomark" color="negative" />
 */
export const Logo = forwardRef<SVGSVGElement, LogoProps>(
  ({ product = 'wsuite', variant = 'logo', color = 'positive', sx, ...props }, ref) => {
    const rawId = useId();
    const id = `forest-logo-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const resolvedColor = resolveColor(product, variant, color);
    const renderer = svgContent[product][variant][resolvedColor];
    const content = renderer ? renderer(id) : null;
    return (
      <Box
        component="svg"
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={viewBoxMap[product][variant]}
        fill="none"
        sx={{
          display: 'block',
          width: 'auto',
          height: 32,
          ...sx,
        }}
        {...props}
      >
        {content}
      </Box>
    );
  }
);

Logo.displayName = 'Logo';
